import useAnalytics from '@hooks/useAnalytics';
import {
  getSortedHours,
  isTimeSlotInSelectedDay,
  isTimeslotInTheFuture,
  parseDay,
  useCookieStore,
  useNavigationStore,
  useServiceTreeStore,
} from '@packages/shared';
import { getDateInTimezone, getDateTypeOfDay } from '@packages/shared';
import { Actions, Locales, TimeSlot } from '@packages/types';
import { SpinnerWrapper } from '@packages/ui-web';
import { useTranslation } from 'next-i18next';
import posthog from 'posthog-js';
import { useMemo } from 'react';
import { shallow } from 'zustand/shallow';

import NextAvailableDateButton from './NextAvailableDateButton';
import NoAvailableTimeSlotsBanner from './NoAvailableSlotsBanner';
import SurchargePricingInfo from './SurchargePricingInfo';
import TimeButtons from './TimeButtons';

type TimePickerProps = {
  locale?: Locales;
  timeSlots: TimeSlot[];
  selectedDate: string;
  isLoading?: boolean;
  daysAhead: number;
  nextAvailableTimeSlot?: { startTime: string };
  setSelectedDate?: (date: string) => void;
  onClick: (time?: string, practitionerId?: string) => void;
  practitionerId?: string;
  price?: number;
};

export default function TimePicker({
  timeSlots,
  selectedDate,
  isLoading,
  practitionerId,
  onClick,
  setSelectedDate,
  nextAvailableTimeSlot,
  locale = 'nb',
  price = 0,
}: TimePickerProps) {
  const now = new Date();
  const { t } = useTranslation(['common', 'booking']);
  const setEventProps = useAnalytics();
  const { queries } = useNavigationStore((state) => ({
    setQueries: state.setQueries,
    queries: state.queries,
  }));
  const { getSurcharge } = useServiceTreeStore(
    (state) => ({
      getSurcharge: state.getSurcharge,
    }),
    shallow,
  );
  const isPosthogEnabled = useCookieStore((state) => state.isPosthogEnabled);

  const availableTimeSlotsOnSelectedDay = timeSlots.filter(({ startTime }) => {
    return (
      isTimeslotInTheFuture(startTime, now) &&
      isTimeSlotInSelectedDay(startTime, parseDay(selectedDate))
    );
  });

  const sortedHours = useMemo(() => {
    return getSortedHours(availableTimeSlotsOnSelectedDay || []);
  }, [availableTimeSlotsOnSelectedDay]);

  const hours = useMemo(() => {
    // If there are no available time slots on the
    // selected day, show the next available time slot
    const availableHours = Object.entries(sortedHours).filter((period) => {
      const isAvailable =
        period[1].length > 0 &&
        period[1].some((slot) => {
          return slot.isAvailable;
        });
      return isAvailable;
    });

    return availableHours;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedHours]);

  const nextAvailableTest =
    isPosthogEnabled &&
    posthog?.getFeatureFlag('practitioner-next-available-v2') === 'test';

  const nextAvailableDate =
    nextAvailableTimeSlot &&
    getDateInTimezone(new Date(nextAvailableTimeSlot.startTime));

  const nextAvailableOnClick = (
    setSelectedDate: (date: string) => void,
    nextAvailableDate: string,
  ) => {
    setSelectedDate(nextAvailableDate);
    window.scrollTo(0, 0);
    setEventProps({
      event: {
        action: Actions.ClickNextAvailable,
        practitionerId: practitionerId,
        nextAvailableDate,
        selectedDate,
      },
    });
  };

  const typeOfDay = getDateTypeOfDay(selectedDate);

  return (
    <div className="w-full">
      <div className="flex h-full flex-col items-center justify-center gap-8">
        {isLoading ? (
          <div data-testid="timeslot-loading-spinner">
            <SpinnerWrapper testId="loading-timeslots" />
          </div>
        ) : (
          hours.map(([period, timeslots]) => {
            const { surchargePrice, surchargeType } = getSurcharge(
              queries.service,
              typeOfDay,
              period === 'evening',
              price === 0,
            );

            return (
              <div key={period} className="flex w-full flex-col justify-start">
                <div className="flex flex-col gap-4">
                  <h2 className="text-base font-medium">{t(period)}</h2>
                  {surchargeType && (
                    <SurchargePricingInfo
                      price={price}
                      surcharge={surchargePrice}
                      surchargeType={surchargeType}
                      className="text-xs"
                    />
                  )}
                  <TimeButtons
                    timeSlots={timeslots}
                    onClick={(time) => onClick(time, practitionerId)}
                  />
                </div>
              </div>
            );
          })
        )}
        {hours.length === 0 &&
          !isLoading &&
          (setSelectedDate && nextAvailableDate && nextAvailableTest ? (
            <NextAvailableDateButton
              nextAvailableDate={nextAvailableDate}
              locale={locale}
              onClick={() =>
                nextAvailableOnClick(setSelectedDate, nextAvailableDate)
              }
            />
          ) : (
            <NoAvailableTimeSlotsBanner />
          ))}
      </div>
    </div>
  );
}
